import React from 'react';
import { MusicTheory } from '../music-theory';

interface KeyButtonsProps {
    onKeySelect: (key: string) => void;
    disabled?: boolean;
}

const KeyButtons: React.FC<KeyButtonsProps> = ({ onKeySelect, disabled = false }) => {
    // Circle of fifths order for better musical layout
    // Starting from C and going clockwise: C, G, D, A, E, B, F#, Db, Ab, Eb, Bb, F
    const keysInCircleOfFifths = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];

    // Get button styling based on whether it's a sharp/flat key
    const getButtonStyle = (key: string) => {
        const baseStyle = `
            flex-1 min-w-0 py-4 sm:py-3 px-2 rounded-lg sm:rounded-xl font-medium text-base sm:text-sm md:text-base
            transition-all duration-200 active:scale-95 shadow-md hover:shadow-lg
            disabled:opacity-50 disabled:cursor-not-allowed
            min-h-[48px] touch-manipulation
        `;

        if (MusicTheory.isAccidental(key)) {
            // Sharp/flat keys - darker styling
            return `${baseStyle} bg-slate-800 hover:bg-slate-700 text-white border-2 border-slate-600`;
        } else {
            // Natural keys - lighter styling
            return `${baseStyle} bg-white hover:bg-slate-50 text-slate-800 border-2 border-slate-300`;
        }
    };

    // Get the display name for the key (convert # and b to symbols)
    const getDisplayName = (key: string) => {
        return key.replace('#', '♯').replace('b', '♭');
    };

    // Get the speakable name for accessibility
    const getSpeakableName = (key: string) => {
        return MusicTheory.getSpeakableKey(key);
    };

    return (
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-lg border border-white/20">
            {/* Simplified header */}
            <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-3 text-center">
                Select the key
            </h3>

            {/* Key buttons grid - optimized for mobile touch */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2 sm:gap-3">
                {keysInCircleOfFifths.map((key) => (
                    <button
                        key={key}
                        onClick={() => onKeySelect(key)}
                        disabled={disabled}
                        className={getButtonStyle(key)}
                        aria-label={`Key of ${getSpeakableName(key)} major`}
                    >
                        {getDisplayName(key)}
                    </button>
                ))}
            </div>

            {/* Helper text - hide on mobile to save space */}
            <p className="text-xs sm:text-sm text-slate-500 text-center mt-3 hidden sm:block">
                Keys arranged in circle of fifths order
            </p>
        </div>
    );
};

export default KeyButtons;